import { FC, useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  styled,
  Container,
  Grid,
  TextField,
  Box,
  Button,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import {
  updateUserAchievementsRequestType,
  updateUserAchievementRequestType,
  languageType
} from "../common/type";
import {
  useAuth0Token,
  getUserAchievements,
  updateUserAchievements
} from "../common/http-requests";
import { userAchievementsRequestType } from "../common/type";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Toolbar } from "../components/FooterToolbar";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import {
  AddCircleOutline as AddIcon,
  DeleteOutline as DeleteIcon,
  HelpOutlineOutlined as HelpOutlineOutlinedIcon
} from '@mui/icons-material';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import { basicColor, themeColors } from "../common/colors";
import { pageMt } from "../common/util";
import {
  useIsReadOnly,
  useUserId,
  useLanguage,
} from "../hooks/useStoreGetters";

const LINE_CHART_SIZE: number[] = [
  800, 320
];

const Title = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  margin: 'auto',
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
  textAlign: 'left',
  marginBottom: '2rem',
}));

const StyledLabel = styled('label')({
  position: 'relative',
  width: '100%',
  color: `${themeColors[0]}`,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: `${themeColors[0]}`,
  },
});

const StyledTextField = styled(TextField)({
  position: 'relative',
  width: '100%',
  border: 'none',
  background: 'none',
  paddingRight: '4rem',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '4px',
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: `${themeColors[0]}`,
  },
});

const StyledTooltip = styled(Tooltip)({
  color: `${themeColors[0]}`,
  position: 'absolute',
  right: '0',
  bottom: '-.2rem',
});

const StyledIconButton = styled(IconButton)({
  color: `${themeColors[0]}`,
  position: 'absolute',
  right: '0',
  bottom: '0',
});

const UserAchievementsPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const isReadOnly = useIsReadOnly();
  const userId = useUserId();
  const language = useLanguage();
  const { isDesktop } = useCustomMediaQuery();

  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });
  const [formData, setFormData] = useState<updateUserAchievementRequestType[]>([]);

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    const requestParams: updateUserAchievementsRequestType = {
      login_user_id: userId,
      achievements: formData
    }
    try {
      const token = await getToken();
      await updateUserAchievements(token, requestParams);

      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.saved"),
        type: "success",
      });
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getUserAchievementsData(userId);
      }

    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getUserAchievementsData = async (login_user_id: number) => {
    setIsSending(true);
    try {
      const requestParams: userAchievementsRequestType = {
        login_user_id,
      };
      const data = await getUserAchievements(await getToken(), requestParams);
      setFormData(data.achievements);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleAddField = (achievement_type: string, index: number): void => {
    const newEntry: updateUserAchievementRequestType = {
      achievement_type: achievement_type,
      achievement_value: "",
    };
    const updatedFormData = [...formData];
    updatedFormData.splice(index + 1, 0, newEntry);
    setFormData(updatedFormData);
  };

  const handleRemoveField = (index: string, typeIndex?: number) => {
    const updatedData = [...formData];
    if (typeIndex === 0) {
      updatedData[Number(index)] = { ...updatedData[Number(index)], achievement_value: '' };
    } else {
      updatedData.splice(Number(index), 1);
    }
    setFormData(updatedData);
  };

  const handleChange = (index: string, event: any) => {
    const updatedData = [...formData]; // 配列のコピーを作成
    updatedData[Number(index)] = {
      achievement_type: updatedData[Number(index)].achievement_type,
      achievement_value: event.target.value
    };
    setFormData(updatedData); // 更新された配列をセット
  };

  const [openStates, setOpenStates] = useState<{ [key: string]: boolean }>({});
  const handleTooltipOpen = (index: string) => {
    setOpenStates((prev) => ({ ...prev, [index]: true }));
  };

  const handleTooltipClose = (index: string) => {
    setOpenStates((prev) => ({ ...prev, [index]: false }));
  };
  const renderFields = (achievement_type: string) => {
    const label: string = t("userAchievements.arch_" + achievement_type);
    const helpText: string = language == languageType.Japanese
      ? label + " を100文字以内で入力してください"
      : "Please enter your " + label + " in 100 characters or less";

    let typeCount = 0;
    let typeIndex = -1;
    const fieldFilter = (field_achievement_type: string, render_achievement_type: string) => {
      if (field_achievement_type === render_achievement_type) {
        ++typeIndex
        return true
      } else { ++typeCount }
    }
    return (
      <>
        {formData.length && formData.map((field: updateUserAchievementRequestType, index: number) => (
          (fieldFilter(field.achievement_type, achievement_type)) ? (
            <FieldComponent
              key={index}
              achievement_type={achievement_type}
              label={label}
              helpText={helpText}
              field={field}
              index={index}
              typeIndex={typeIndex}
              openStates={openStates}
              isReadOnly={isReadOnly}
              handleTooltipOpen={handleTooltipOpen}
              handleTooltipClose={handleTooltipClose}
              handleChange={handleChange}
              handleAddField={handleAddField}
              handleRemoveField={handleRemoveField}
            />
          ) : null
        ))}
        {(formData.length === typeCount) && handleAddField(achievement_type, typeIndex)}
      </>
    )
  };

  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            />
            <Box mt={pageMt(isDesktop)} className="mb-2 p-2 w-[80%] mx-auto" maxWidth={`${LINE_CHART_SIZE[0]}px`}>
              <Title>
                <Typography
                  variant="h5"
                  style={{ marginTop: 10, textAlign: "left" }}
                >
                  <StarBorderOutlinedIcon sx={{ margin: '.5rem .8rem' }} />{t("menu.userAchievements")}
                </Typography>
              </Title>
              <Box>
              </Box>
              {renderFields('1')}
              {renderFields('2')}
              {renderFields('3')}
              {renderFields('4')}
              {renderFields('5')}
              {renderFields('6')}
              {renderFields('7')}
              {renderFields('8')}
              <Box
                sx={{
                  width: '20%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 'fit-content',
                  margin: '2rem auto',
                }}
              >
                <Button
                  style={{
                    width: '100px',
                    marginLeft: '20px',
                    fontSize: '16px',
                    alignItems: 'center',
                    backgroundColor: basicColor,
                  }}
                  variant="contained"
                  disabled={isReadOnly}
                  onClick={handleSubmit}
                >
                  {t("button.save")}
                </Button>
              </Box>
            </Box>
          </>
        )}
      <Toolbar />
    </Container>
  );
};
interface FieldComponentProps {
  achievement_type: string;
  label: string;
  helpText?: string;
  field: updateUserAchievementRequestType;
  index: number;
  typeIndex: number;
  openStates: { [key: string]: boolean };
  isReadOnly: boolean;
  handleTooltipOpen: (index: string) => void;
  handleTooltipClose: (index: string) => void;
  handleChange: (id: string, event: any) => void;
  handleAddField: (category: string, index: number) => void;
  handleRemoveField: (index: string, typeIndex: number) => void;
}

const TextFieldMaxLength = 100;
const FieldComponent: React.FC<FieldComponentProps> = ({
  achievement_type,
  label,
  helpText,
  field,
  index,
  typeIndex,
  openStates,
  isReadOnly,
  handleTooltipOpen,
  handleTooltipClose,
  handleChange,
  handleAddField,
  handleRemoveField
}) => (

  <Grid container spacing={2} my={2}>
    <Grid item xs={12} key={index}>
      <Grid container alignItems="center">
        <Grid item xs={4}>
          {typeIndex === 0 && (<Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {helpText && (
              <StyledLabel htmlFor={`${achievement_type}_${index}`}>
                {label}
                <StyledTooltip
                  title={helpText}
                  open={openStates["isOpen" + achievement_type + '_' + index] || false}
                  onOpen={() => handleTooltipOpen("isOpen" + achievement_type + '_' + index)}
                  onClose={() => handleTooltipClose("isOpen" + achievement_type + '_' + index)}
                >
                  <IconButton
                    onMouseEnter={() => handleTooltipOpen("isOpen" + achievement_type + '_' + index)}
                    onMouseLeave={() => handleTooltipClose("isOpen" + achievement_type + '_' + index)}
                    onTouchStart={() => handleTooltipOpen("isOpen" + achievement_type + '_' + index)}
                    onTouchEnd={() => handleTooltipClose("isOpen" + achievement_type + '_' + index)}
                  >
                    <HelpOutlineOutlinedIcon />
                  </IconButton>
                </StyledTooltip>
              </StyledLabel>
            )}
          </Box>)}
        </Grid>
        <Grid item xs={0.3}></Grid>
        <Grid item xs={7.7} position={'relative'}>
          <StyledTextField
            id={`${achievement_type}_${index}`}
            name={`${achievement_type}_${index}`}
            variant="standard"
            disabled={isReadOnly}
            fullWidth
            InputProps={{ disableUnderline: true }}
            inputProps={{ maxLength: TextFieldMaxLength }}
            value={field.achievement_value}
            onChange={(event) => handleChange(index.toString(), event)}
          />
          <StyledIconButton
            sx={{ right: '2rem' }}
            disabled={isReadOnly}
            onClick={() => handleAddField(achievement_type, index)}
          >
            <AddIcon />
          </StyledIconButton>
          <StyledIconButton
            disabled={isReadOnly}
            onClick={() => handleRemoveField(index.toString(), typeIndex)}
          >
            <DeleteIcon />
          </StyledIconButton>
          {/* <Box style={{position: 'absolute', top: '30px', right: '10px'}}>
            {field.achievement_value.length}/{TextFieldMaxLength}
          </Box> */}
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);
export default UserAchievementsPage;